import { useState, useEffect } from 'react';
import {
  ClipboardCheck,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Clock,
  CheckCircle2,
  Archive,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Target,
  BarChart3,
  FileText,
  Zap,
  Eye,
} from 'lucide-react';
import { fetchApi } from '../api/client';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STATUS_CONFIG = {
  pending: {
    color: 'amber',
    bg: 'bg-amber-500/20',
    text: 'text-amber-400',
    border: 'border-amber-500/30',
    dot: 'bg-amber-400',
    icon: Clock,
    label: 'Pending',
  },
  scored: {
    color: 'emerald',
    bg: 'bg-emerald-500/20',
    text: 'text-emerald-400',
    border: 'border-emerald-500/30',
    dot: 'bg-emerald-400',
    icon: CheckCircle2,
    label: 'Scored',
  },
  archived: {
    color: 'slate',
    bg: 'bg-slate-500/20',
    text: 'text-slate-400',
    border: 'border-slate-600/30',
    dot: 'bg-slate-500',
    icon: Archive,
    label: 'Archived',
  },
};

function getPrep(sc) {
  const fields = [
    { key: 'consensus', label: 'Consensus', ok: !!sc.consensus },
    { key: 'scorecard_metrics', label: 'Metrics', ok: Array.isArray(sc.scorecard_metrics) && sc.scorecard_metrics.length > 0 },
    { key: 'implied_move', label: 'Implied Move', ok: !!sc.implied_move },
    { key: 'scenarios', label: 'Scenarios', ok: Array.isArray(sc.scenarios) && sc.scenarios.length > 0 },
    { key: 'portfolio_readthrough', label: 'Readthrough', ok: !!sc.portfolio_readthrough },
  ];
  const done = fields.filter(f => f.ok).length;
  return { fields, done, total: fields.length };
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function daysFromNow(iso) {
  if (!iso) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const target = new Date(iso + 'T00:00:00');
  return Math.ceil((target - now) / (1000 * 60 * 60 * 24));
}

function truncate(str, len = 60) {
  if (!str) return '';
  return str.length > len ? str.slice(0, len) + '...' : str;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function PrepDots({ prep }) {
  return (
    <div className="flex items-center gap-1" title={`${prep.done}/${prep.total} prepped`}>
      {prep.fields.map((f, i) => (
        <div
          key={i}
          className={`w-2 h-2 rounded-full ${f.ok ? 'bg-amber-400' : 'bg-slate-600'}`}
          title={`${f.label}: ${f.ok ? 'Ready' : 'Not set'}`}
        />
      ))}
      <span className="text-xs text-slate-500 ml-1">{prep.done}/{prep.total}</span>
    </div>
  );
}

function ConsensusSection({ consensus }) {
  if (!consensus) return <EmptyField label="Consensus" />;
  return (
    <div className="space-y-1">
      <SectionLabel icon={Target} label="Consensus" />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {consensus.eps !== undefined && (
          <MiniStat label="EPS" value={`$${consensus.eps}`} />
        )}
        {consensus.revenue !== undefined && (
          <MiniStat label="Revenue" value={typeof consensus.revenue === 'number' ? `$${(consensus.revenue / 1e9).toFixed(2)}B` : consensus.revenue} />
        )}
        {consensus.guidance_key_metric && (
          <MiniStat label="Guidance Key" value={consensus.guidance_key_metric} />
        )}
      </div>
    </div>
  );
}

function ImpliedMoveSection({ data }) {
  if (!data) return <EmptyField label="Implied Move" />;
  return (
    <div className="space-y-1">
      <SectionLabel icon={Zap} label="Implied Move" />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {data.pct !== undefined && <MiniStat label="Move" value={`${data.pct > 0 ? '+' : ''}${data.pct}%`} />}
        {data.range_low !== undefined && data.range_high !== undefined && (
          <MiniStat label="Range" value={`$${data.range_low} – $${data.range_high}`} />
        )}
        {data.straddle_price !== undefined && <MiniStat label="Straddle" value={`$${data.straddle_price}`} />}
      </div>
    </div>
  );
}

function OiWallsSection({ data }) {
  if (!data) return null;
  return (
    <div className="space-y-1">
      <SectionLabel icon={BarChart3} label="OI Walls" />
      <div className="grid grid-cols-2 gap-3">
        {data.resistance && data.resistance.length > 0 && (
          <div>
            <span className="text-xs text-red-400 font-medium">Resistance</span>
            <div className="text-sm text-slate-300">
              {data.resistance.slice(0, 3).map((r, i) => (
                <span key={i} className="mr-2">${typeof r === 'object' ? r.strike || r.price : r}</span>
              ))}
            </div>
          </div>
        )}
        {data.support && data.support.length > 0 && (
          <div>
            <span className="text-xs text-emerald-400 font-medium">Support</span>
            <div className="text-sm text-slate-300">
              {data.support.slice(0, 3).map((s, i) => (
                <span key={i} className="mr-2">${typeof s === 'object' ? s.strike || s.price : s}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MetricsTable({ metrics }) {
  if (!metrics || metrics.length === 0) return <EmptyField label="Scorecard Metrics" />;
  return (
    <div className="space-y-1">
      <SectionLabel icon={BarChart3} label="Scorecard Metrics" />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-slate-500 border-b border-slate-700/50">
              <th className="text-left py-1 pr-4">Metric</th>
              <th className="text-right py-1 px-2">Weight</th>
              <th className="text-right py-1 px-2">Bull</th>
              <th className="text-right py-1 px-2">Bear</th>
            </tr>
          </thead>
          <tbody>
            {metrics.map((m, i) => (
              <tr key={i} className="border-b border-slate-700/30">
                <td className="py-1 pr-4 text-slate-300">{m.name || m.metric}</td>
                <td className="py-1 px-2 text-right text-slate-400">{m.weight ? `${m.weight}%` : '—'}</td>
                <td className="py-1 px-2 text-right text-emerald-400">{m.bull_threshold ?? '—'}</td>
                <td className="py-1 px-2 text-right text-red-400">{m.bear_threshold ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ScenariosSection({ scenarios, played }) {
  if (!scenarios || scenarios.length === 0) return <EmptyField label="Scenarios" />;
  return (
    <div className="space-y-1">
      <SectionLabel icon={Eye} label="Scenarios" />
      <div className="space-y-2">
        {scenarios.map((s, i) => {
          const isPlayed = played && s.name === played;
          return (
            <div
              key={i}
              className={`p-2 rounded-lg border ${
                isPlayed
                  ? 'bg-emerald-500/10 border-emerald-500/30'
                  : 'bg-slate-800/30 border-slate-700/30'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className={`font-medium text-sm ${isPlayed ? 'text-emerald-400' : 'text-slate-200'}`}>
                  {s.name}
                </span>
                {s.probability && (
                  <span className="text-xs text-slate-500">{s.probability}%</span>
                )}
                {isPlayed && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400">PLAYED</span>
                )}
              </div>
              {s.description && <p className="text-xs text-slate-400 mt-0.5">{s.description}</p>}
              {s.action && <p className="text-xs text-sky-400 mt-0.5">Action: {s.action}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ScoresSection({ scores }) {
  if (!scores || scores.length === 0) return null;
  return (
    <div className="space-y-1">
      <SectionLabel icon={CheckCircle2} label="Scores" />
      <div className="space-y-1">
        {scores.map((s, i) => {
          const score = s.score ?? s.result;
          const isBull = typeof score === 'string' ? score.toLowerCase().includes('beat') || score.toLowerCase().includes('bull') : score > 0;
          return (
            <div key={i} className="flex items-center justify-between py-1 border-b border-slate-700/30 last:border-0">
              <span className="text-sm text-slate-300">{s.name || s.metric}</span>
              <div className="flex items-center gap-2">
                {s.weight && <span className="text-xs text-slate-500">{s.weight}%</span>}
                <span className={`text-sm font-medium ${isBull ? 'text-emerald-400' : 'text-red-400'}`}>
                  {typeof score === 'number' ? (score > 0 ? '+' : '') + score : score}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ReadthroughSection({ text }) {
  if (!text) return <EmptyField label="Portfolio Readthrough" />;
  return (
    <div className="space-y-1">
      <SectionLabel icon={FileText} label="Portfolio Readthrough" />
      <p className="text-sm text-slate-300 whitespace-pre-wrap">{text}</p>
    </div>
  );
}

function DecisionSection({ decision, rationale }) {
  if (!decision) return null;
  return (
    <div className="space-y-1">
      <SectionLabel icon={Target} label="Decision" />
      <p className="text-sm text-slate-200 font-medium">{decision}</p>
      {rationale && <p className="text-sm text-slate-400 mt-1">{rationale}</p>}
    </div>
  );
}

function ActualResultsSection({ data }) {
  if (!data) return null;
  return (
    <div className="space-y-1">
      <SectionLabel icon={BarChart3} label="Actual Results" />
      {typeof data === 'object' && !Array.isArray(data) ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Object.entries(data).map(([k, v]) => (
            <MiniStat key={k} label={k.replace(/_/g, ' ')} value={String(v)} />
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-300">{JSON.stringify(data)}</p>
      )}
    </div>
  );
}

function SectionLabel({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500 uppercase tracking-wide">
      <Icon className="w-3.5 h-3.5" />
      {label}
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="bg-slate-800/50 rounded-lg px-3 py-2">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-sm font-medium text-slate-200">{value}</div>
    </div>
  );
}

function EmptyField({ label }) {
  return (
    <div className="flex items-center gap-2 py-1">
      <AlertCircle className="w-3.5 h-3.5 text-slate-600" />
      <span className="text-xs text-slate-600">{label} — not set</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scorecard Card
// ---------------------------------------------------------------------------

function ScorecardCard({ sc, isLast }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = STATUS_CONFIG[sc.status] || STATUS_CONFIG.pending;
  const SevIcon = cfg.icon;
  const prep = getPrep(sc);
  const days = daysFromNow(sc.earnings_date);

  return (
    <div className="flex gap-4">
      {/* Timeline connector */}
      <div className="flex flex-col items-center shrink-0">
        <div className={`w-3 h-3 rounded-full ${cfg.dot} ring-2 ring-slate-800 mt-4`} />
        {!isLast && <div className="w-0.5 flex-1 bg-slate-700/50 mt-1" />}
      </div>

      {/* Card */}
      <div className={`flex-1 mb-4 rounded-lg border ${cfg.border} bg-slate-800/30 overflow-hidden`}>
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full text-left p-3 hover:bg-slate-700/20 transition-colors"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <span className="font-mono font-bold text-slate-200 text-sm">{sc.ticker}</span>
              <span className="text-xs text-slate-500">{sc.fiscal_quarter}</span>
              <span className={`text-xs px-1.5 py-0.5 rounded ${cfg.bg} ${cfg.text} flex items-center gap-1`}>
                <SevIcon className="w-3 h-3" />
                {cfg.label}
              </span>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {/* Earnings date + days away */}
              <div className="text-right hidden sm:block">
                <div className="text-xs text-slate-400">{formatDate(sc.earnings_date)}</div>
                {days !== null && sc.status === 'pending' && (
                  <div className={`text-xs font-medium ${
                    days <= 2 ? 'text-red-400' : days <= 7 ? 'text-amber-400' : 'text-slate-500'
                  }`}>
                    {days <= 0 ? 'Today' : `${days}d away`}
                  </div>
                )}
              </div>
              {expanded ? (
                <ChevronUp className="w-4 h-4 text-slate-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-slate-400" />
              )}
            </div>
          </div>

          {/* Summary row */}
          <div className="flex items-center gap-3 mt-1.5">
            {sc.status === 'pending' && <PrepDots prep={prep} />}
            {sc.status === 'scored' && sc.scenario_that_played && (
              <span className="text-xs text-emerald-400">
                Scenario: {sc.scenario_that_played}
              </span>
            )}
            {sc.status === 'scored' && sc.decision_made && (
              <span className="text-xs text-slate-400 truncate">
                {truncate(sc.decision_made)}
              </span>
            )}
            {sc.status === 'archived' && sc.scenario_that_played && (
              <span className="text-xs text-slate-500">
                {sc.scenario_that_played} — {truncate(sc.decision_made, 40)}
              </span>
            )}
          </div>
        </button>

        {/* Expanded detail */}
        {expanded && (
          <div className="px-3 pb-3 space-y-4 border-t border-slate-700/30 pt-3">
            {/* Pre-earnings fields */}
            <ConsensusSection consensus={sc.consensus} />
            <ImpliedMoveSection data={sc.implied_move} />
            <OiWallsSection data={sc.oi_walls} />
            <MetricsTable metrics={sc.scorecard_metrics} />
            <ScenariosSection scenarios={sc.scenarios} played={sc.scenario_that_played} />
            <ReadthroughSection text={sc.portfolio_readthrough} />

            {/* Post-earnings fields (scored/archived only) */}
            {(sc.status === 'scored' || sc.status === 'archived') && (
              <div className="border-t border-slate-700/30 pt-3 space-y-4">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm font-medium text-emerald-400">Post-Earnings Results</span>
                  {sc.scored_at && (
                    <span className="text-xs text-slate-500">Scored {formatDate(sc.scored_at.split('T')[0])}</span>
                  )}
                </div>
                <ActualResultsSection data={sc.actual_results} />
                <ScoresSection scores={sc.scores} />
                <DecisionSection decision={sc.decision_made} rationale={sc.decision_rationale} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function EarningsScorecards() {
  const [scorecards, setScorecards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadScorecards();
  }, [showArchived]);

  async function loadScorecards() {
    setLoading(true);
    setError(null);
    try {
      const fetches = [
        fetchApi('/scorecards?limit=30').catch(() => ({ scorecards: [] })),
      ];
      if (showArchived) {
        fetches.push(
          fetchApi('/scorecards?status=archived&limit=20').catch(() => ({ scorecards: [] }))
        );
      }
      const results = await Promise.all(fetches);
      let all = results.flatMap(r => r.scorecards || []);

      // Deduplicate by id
      const seen = new Set();
      all = all.filter(sc => {
        if (seen.has(sc.id)) return false;
        seen.add(sc.id);
        return true;
      });

      // Sort: pending first (by earnings_date asc), then scored (by earnings_date desc), then archived
      all.sort((a, b) => {
        const statusOrder = { pending: 0, scored: 1, archived: 2 };
        const sa = statusOrder[a.status] ?? 9;
        const sb = statusOrder[b.status] ?? 9;
        if (sa !== sb) return sa - sb;
        // Within same status: pending by date ascending (soonest first), others descending
        if (a.status === 'pending') {
          return (a.earnings_date || '').localeCompare(b.earnings_date || '');
        }
        return (b.earnings_date || '').localeCompare(a.earnings_date || '');
      });

      setScorecards(all);
    } catch (err) {
      console.error('Scorecards load error:', err);
      setError('Failed to load scorecards');
    } finally {
      setLoading(false);
    }
  }

  const counts = {
    pending: scorecards.filter(s => s.status === 'pending').length,
    scored: scorecards.filter(s => s.status === 'scored').length,
    archived: scorecards.filter(s => s.status === 'archived').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-100 flex items-center gap-2">
            <ClipboardCheck className="w-6 h-6 text-sky-400" />
            Earnings Scorecards
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            {counts.pending > 0 && <span className="text-amber-400">{counts.pending} pending</span>}
            {counts.pending > 0 && counts.scored > 0 && <span> · </span>}
            {counts.scored > 0 && <span className="text-emerald-400">{counts.scored} scored</span>}
            {showArchived && counts.archived > 0 && (
              <><span> · </span><span className="text-slate-500">{counts.archived} archived</span></>
            )}
            {counts.pending === 0 && counts.scored === 0 && !loading && (
              <span>No active scorecards</span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Archive toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <span className="text-xs text-slate-400">Show Archived</span>
            <button
              onClick={() => setShowArchived(!showArchived)}
              className={`relative w-9 h-5 rounded-full transition-colors ${
                showArchived ? 'bg-sky-500' : 'bg-slate-600'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                  showArchived ? 'translate-x-4' : ''
                }`}
              />
            </button>
          </label>

          {/* Refresh */}
          <button
            onClick={loadScorecards}
            className="p-2 rounded-lg hover:bg-slate-700/50 transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 text-slate-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-400">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && scorecards.length === 0 && (
        <div className="flex items-center justify-center py-16">
          <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
        </div>
      )}

      {/* Empty state */}
      {!loading && scorecards.length === 0 && (
        <div className="text-center py-16">
          <ClipboardCheck className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">No earnings scorecards yet</p>
          <p className="text-sm text-slate-500 mt-1">
            Scorecards are created via the copilot before earnings reports
          </p>
        </div>
      )}

      {/* Timeline */}
      {scorecards.length > 0 && (
        <div className="pl-1">
          {scorecards.map((sc, i) => (
            <ScorecardCard key={sc.id} sc={sc} isLast={i === scorecards.length - 1} />
          ))}
        </div>
      )}
    </div>
  );
}
